import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { Button } from 'antd';
// import Select from 'rc-select';
import { useTranslation } from 'react-i18next';
import { filterSameColor, iconButtonStyle, navbarButtonStyle } from '../../utils/styles'
import { useStore } from '../../store/store';
import { Checkable } from '../common/Checkable';
import DuotoneLightIcon from '../common/DuotoneLightIcon';
import {
  handleBorderChangement,
  onChangeBorderColor,
  hasAllBordersOn,
  getBoxBorder,
  getBoxBorderColor
} from '../RichEditor/utils';
import { NavbarListButton } from './NavbarListButton';
import { useCallback } from 'react';
import { isASelectionBox, isATextBox, isAVariableBox, isAImgBox } from '../../utils/boxes';
import { ItemTypes } from '../../constants/constants';
import _ from 'lodash';

const selector = ({ updateBox, configuration, selectedBoxId, boxes, masks, containers, currentProposal }) => {
  const box = [
    ...boxes,
    ...masks.map((m) => m.boxes).flat(),
  ].find((box) => selectedBoxId && box.id === selectedBoxId)
  const container = containers.find(container => container.columnsIds.includes(box?.columnId))

  return {
    updateBox,
    configuration,
    selectedBox: box,
    container: container,
    currentProposal,
    isSelectionBox: isASelectionBox(boxes.find((b) => b.id === selectedBoxId)?.type),
  }
};

const borderPositionButtons = (box) => {
  return [
    {
      key: "borderAll",
      icon: < FontAwesomeIcon icon={icon({ name: 'border-outer', style: 'light' })} style={iconButtonStyle()} />,
      sides: ['left', 'top', 'right', 'bottom'],
      active: hasAllBordersOn(box)
    },
    {
      key: "borderLeft",
      icon: < FontAwesomeIcon icon={icon({ name: 'border-left', style: 'light' })} style={iconButtonStyle()} />,
      sides: ['left'],
      active: getBoxBorder(box, "left")
    },
    {
      key: "borderTop",
      icon: < FontAwesomeIcon icon={icon({ name: 'border-top', style: 'light' })} style={iconButtonStyle()} />,
      sides: ['top'],
      active: getBoxBorder(box, "top")
    },
    {
      key: "borderRight",
      icon: < FontAwesomeIcon icon={icon({ name: 'border-right', style: 'light' })} style={iconButtonStyle()} />,
      sides: ['right'],
      active: getBoxBorder(box, "right")
    },
    {
      key: "borderBottom",
      icon: < FontAwesomeIcon icon={icon({ name: 'border-bottom', style: 'light' })} style={iconButtonStyle()} />,
      sides: ['bottom'],
      active: getBoxBorder(box, "bottom")
    },
  ]
};

// `part` : quels boutons rendre. 'all' par défaut (barre d'icônes historique) ;
// 'border' ou 'color' pour les panneaux qui donnent un libellé à chaque réglage
// et ont donc besoin des deux boutons sur deux lignes distinctes.
export const BorderManager = ({ part = 'all' }) => {
  const { t } = useTranslation();
  const { updateBox, configuration, selectedBox, container, currentProposal, isSelectionBox } = useStore(selector);

  const borderColorDisabled =
    selectedBox &&
    [
      'title1',
      'title2',
      'title3',
      'title4',
      'summary',
      'table',
      'line',
      ItemTypes.TABLE_VARIABLE
    ].includes(selectedBox.type);
  // const borderColorsEnabled = true;
  const selectedBoxIsShape =
    selectedBox?.type === 'shape' || selectedBox?.type === 'line';

  const toggleBorderColor = (newColor) => {
    if (selectedBoxIsShape) {
      updateBox(selectedBox.id, (selectedBox) => {
        selectedBox.borders.color = configuration.colors.find(
          (color) => color.label === newColor
        );
      });
    } else {
      onChangeBorderColor(
        selectedBox.id,
        updateBox,
        configuration
      )(newColor);
    }
  }

  const unCommonBox =
    !selectedBox ||
    ['text', 'title1', 'title2', 'title3', 'title4', 'summary', 'table', 'line_break', ItemTypes.GROUP_BOXES].includes(selectedBox?.type) ||
    isAVariableBox(selectedBox?.type)

  const backgroundManagerDisabled = useCallback(() => {
    if (isSelectionBox) return true
    if (!container?.editable && container?.hasOwnProperty('editable') && currentProposal.id !== null) {
      return true
    } else return unCommonBox || borderColorDisabled
  }, [isSelectionBox, container, currentProposal?.id, unCommonBox, borderColorDisabled])

  const borderManagerDisabled = useCallback(() => {
    if (isSelectionBox) return true
    if (!container?.editable && container?.hasOwnProperty('editable') && currentProposal.id !== null) {
      return true
    } else return unCommonBox || selectedBoxIsShape
  }, [isSelectionBox, container, currentProposal?.id, unCommonBox, selectedBoxIsShape])

  const shouldHideButton = (box, isTextBoxCheck, managerDisabled) => {
    if (!box) return true;

    // Logique spécifique pour les boîtes images
    if (isAImgBox(box?.type)) {
      // Pour les boîtes images, on ne cache jamais les boutons, on les désactive juste
      return false;
    }

    // Logique originale pour les autres types de boîtes
    if (!isTextBoxCheck(box?.type)) return managerDisabled();
    if (_.isEmpty(box?.border) && _.isEmpty(box?.backgroundColor)) return true;
    return box?.backgroundColor?.label === "transparent" && box?.border?.color?.label === "transparent"
      ? managerDisabled()
      : false;
  };

  // Fonction spécifique pour désactiver les boutons des boîtes images
  const isImageBoxDisabled = useCallback(() => {
    return selectedBox && isAImgBox(selectedBox?.type) && isAVariableBox(selectedBox?.type);
  }, [selectedBox]);

  let hideButton = shouldHideButton(selectedBox, isATextBox, borderManagerDisabled);
  let hideButtonColor = shouldHideButton(selectedBox, isATextBox, backgroundManagerDisabled);

  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
      {part !== 'color' && <NavbarListButton
        key="borderPositionList"
        title={t('border')}
        hidden={hideButton}
        disabled={isImageBoxDisabled() || borderManagerDisabled()}
        icon={< FontAwesomeIcon icon={icon({ name: 'border-bottom', style: 'light' })} style={iconButtonStyle()} />}
        selectedElement={selectedBox?.border?.color?.label}
      >
        {selectedBox && !selectedBoxIsShape && borderPositionButtons(selectedBox).map((button) => {
          return (
            <Checkable key={`position${button.sides.join('')}`} checked={button.active}>
              <Button
                style={{
                  ...navbarButtonStyle,
                  marginBottom: '1px',
                }}
                icon={button.icon}
                disabled={isImageBoxDisabled() || borderManagerDisabled()}
                onMouseDown={() => handleBorderChangement(selectedBox.id, updateBox, button.sides)}
              />
            </Checkable>
          )
        })}
      </NavbarListButton>}
      {/* BORDER COLORS */}
      {part !== 'border' && <NavbarListButton
        key="borderColorList"
        title={t("border's-color")}
        disabled={isImageBoxDisabled() || backgroundManagerDisabled()}
        hidden={hideButtonColor}
        icon={<DuotoneLightIcon name='border-outer' style={iconButtonStyle()} />}
        colorInIcon='primary'
        onListElementClick={toggleBorderColor}
        listElements={filterSameColor(configuration.colors.filter((c) => c.label !== 'transparent'))}
        selectedElement={selectedBox && getBoxBorderColor(selectedBox)}
      />}
    </div>
  );
};
